/**
 * Error Handling Utilities for Public Websearch Chatbot Client
 *
 * Provides context-based error handling and response formatting for both chat
 * and feedback flows. Uses execution context instead of complex categorization
 * to determine appropriate error messages.
 */

/**
 * Standard error response structure matching @assistant-ui/react format
 */
export interface ChatErrorResponse {
  content: never[]; // Empty since error text is in status.error
  status: {
    error: string;
    reason: 'error';
    type: 'incomplete';
  };
  metadata?: {
    custom?: {
      sessionId?: string;
    };
  };
}

/**
 * Gets appropriate error message based on error type and context
 */
export function getErrorMessage(
  errorType: 'network' | 'server' | 'auth' | 'abort' | 'unknown',
  context: 'chat' | 'feedback' = 'chat',
): string {
  // Most feedback errors use the same generic message
  if (context === 'feedback') {
    return errorType === 'abort'
      ? 'Request was cancelled.'
      : 'Sorry, there was an error submitting your feedback. Please try again.';
  }

  // Chat context has specific messages for different error types
  switch (errorType) {
    case 'network':
      return 'Unable to connect. Please check your internet connection.';
    case 'server':
      return 'Sorry, something went wrong on our end. Please try again later.';
    case 'auth':
      return 'Your session has expired. Please refresh the page.';
    case 'abort':
      return 'Request was cancelled.';
    default:
      return 'Sorry, something went wrong!';
  }
}

/**
 * Creates a standard error response matching the expected chat message format
 */
export function createErrorResponse(
  message: string,
  sessionId?: string,
): ChatErrorResponse {
  return {
    content: [], // Empty array since error text goes in status.error
    status: {
      error: message,
      reason: 'error',
      type: 'incomplete',
    },
    ...(sessionId && {
      metadata: {
        custom: { sessionId },
      },
    }),
  };
}

/**
 * Logs error details for debugging while avoiding sensitive information
 */
export function logError(
  error: unknown,
  context: string,
  additionalInfo?: Record<string, any>,
): void {
  const errorInfo = {
    context,
    error:
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            // Only include stack trace in development to avoid exposing internal paths
            ...(process.env.NODE_ENV === 'development' && {
              stack: error.stack,
            }),
          }
        : error,
    timestamp: new Date().toISOString(),
    ...additionalInfo,
  };

  console.error('[Chatbot Error]', errorInfo);
}
